import { loadStripe } from "@stripe/stripe-js";
import api from "./api";

// Initialize Stripe with publishable key
let stripePromise = null;

/**
 * Initialize Stripe instance
 * @returns {Promise<Stripe>} Stripe instance
 */
export const getStripe = () => {
  if (!stripePromise) {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    if (!publishableKey) {
      console.warn("Stripe publishable key is not configured - payment features will be unavailable");
      return null;
    }

    stripePromise = loadStripe(publishableKey);
  }

  return stripePromise;
};

/**
 * Stripe Error Types for better error handling
 */
export const STRIPE_ERROR_TYPES = {
  CARD_ERROR: "card_error",
  VALIDATION_ERROR: "validation_error",
  INVALID_REQUEST_ERROR: "invalid_request_error",
  API_CONNECTION_ERROR: "api_connection_error",
  API_ERROR: "api_error",
  AUTHENTICATION_ERROR: "authentication_error",
  RATE_LIMIT_ERROR: "rate_limit_error",
};

/**
 * Enhanced error messages for better UX
 */
const STRIPE_ERROR_MESSAGES = {
  // Card errors
  card_declined:
    "Your card was declined. Please try a different payment method.",
  expired_card: "Your card has expired. Please use a different card.",
  incorrect_cvc:
    "Your card's security code is incorrect. Please check and try again.",
  incorrect_number:
    "Your card number is incorrect. Please check and try again.",
  insufficient_funds:
    "Your card has insufficient funds. Please use a different card.",
  invalid_expiry_month: "Your card's expiration month is invalid.",
  invalid_expiry_year: "Your card's expiration year is invalid.",
  invalid_number: "Your card number is invalid. Please check and try again.",

  // Processing errors
  processing_error:
    "An error occurred while processing your card. Please try again.",

  // Generic fallback
  generic_decline:
    "Your card was declined. Please contact your bank or try a different card.",
};

/**
 * Handle and format Stripe errors for display
 * @param {Object} error - Stripe error object
 * @returns {string} User-friendly error message
 */
export const handleStripeError = (error) => {
  if (!error) return "An unknown error occurred";

  console.error("Stripe error:", error);

  // Check for specific decline codes first
  if (error.decline_code && STRIPE_ERROR_MESSAGES[error.decline_code]) {
    return STRIPE_ERROR_MESSAGES[error.decline_code];
  }

  // Check for error codes
  if (error.code && STRIPE_ERROR_MESSAGES[error.code]) {
    return STRIPE_ERROR_MESSAGES[error.code];
  }

  // Use the error message if it exists and is user-friendly
  if (error.message) {
    return error.message;
  }

  // Fallback to generic message
  return "We couldn't process your payment. Please try again or use a different card.";
};

/**
 * Create a payment intent for subscription
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code (default: 'usd')
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<Object>} Payment intent client secret
 */
export const createPaymentIntent = async (
  amount,
  currency = "usd",
  metadata = {},
) => {
  try {
    const response = await api.post("/stripe/payment-intents", {
      amount,
      currency,
      metadata,
    });

    return response.data;
  } catch (error) {
    console.error("Failed to create payment intent:", error);
    throw new Error(
      error.response?.data?.message || "Failed to initialize payment",
    );
  }
};

/**
 * Create a subscription with payment method
 * @param {Object} subscriptionData - Subscription details
 * @returns {Promise<Object>} Subscription result
 */
export const createSubscription = async (subscriptionData) => {
  try {
    const response = await api.post("/subscriptions", subscriptionData);
    return response.data;
  } catch (error) {
    console.error("Failed to create subscription:", error);
    throw new Error(
      error.response?.data?.message || "Failed to create subscription",
    );
  }
};

/**
 * Confirm payment with 3D Secure handling
 * @param {Object} stripe - Stripe instance
 * @param {string} clientSecret - Payment intent client secret
 * @param {Object} paymentMethod - Payment method object
 * @returns {Promise<Object>} Payment confirmation result
 */
export const confirmPayment = async (
  stripe,
  clientSecret,
  paymentMethod = null,
) => {
  try {
    let confirmationMethod;

    if (paymentMethod) {
      confirmationMethod = {
        payment_method: paymentMethod,
      };
    } else {
      // Let Stripe use the payment method already attached to the PaymentIntent
      confirmationMethod = {};
    }

    const result = await stripe.confirmCardPayment(
      clientSecret,
      confirmationMethod,
    );

    if (result.error) {
      throw result.error;
    }

    return result;
  } catch (error) {
    console.error("Payment confirmation failed:", error);
    throw error;
  }
};

/**
 * Create a payment method
 * @param {Object} stripe - Stripe instance
 * @param {Object} cardElement - Stripe card element
 * @param {Object} billingDetails - Billing information
 * @returns {Promise<Object>} Payment method result
 */
export const createPaymentMethod = async (
  stripe,
  cardElement,
  billingDetails = {},
) => {
  try {
    const result = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: billingDetails,
    });

    if (result.error) {
      throw result.error;
    }

    return result;
  } catch (error) {
    console.error("Failed to create payment method:", error);
    throw error;
  }
};

/**
 * Update payment method for existing subscription
 * @param {string} subscriptionId - Subscription ID
 * @param {string} paymentMethodId - New payment method ID
 * @returns {Promise<Object>} Update result
 */
export const updatePaymentMethod = async (subscriptionId, paymentMethodId) => {
  try {
    const response = await api.put(
      `/subscriptions/${subscriptionId}/payment-method`,
      {
        payment_method_id: paymentMethodId,
      },
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update payment method:", error);
    throw new Error(
      error.response?.data?.message || "Failed to update payment method",
    );
  }
};

/**
 * Get subscription details
 * @param {string} subscriptionId - Subscription ID
 * @returns {Promise<Object>} Subscription details
 */
export const getSubscription = async (subscriptionId) => {
  try {
    const response = await api.get(`/subscriptions/${subscriptionId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to get subscription:", error);
    throw new Error(
      error.response?.data?.message || "Failed to retrieve subscription",
    );
  }
};

/**
 * Cancel subscription
 * @param {string} subscriptionId - Subscription ID
 * @param {boolean} atPeriodEnd - Whether to cancel at period end
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelSubscription = async (
  subscriptionId,
  atPeriodEnd = true,
) => {
  try {
    const response = await api.post(`/subscriptions/${subscriptionId}/cancel`, {
      at_period_end: atPeriodEnd,
    });

    return response.data;
  } catch (error) {
    console.error("Failed to cancel subscription:", error);
    throw new Error(
      error.response?.data?.message || "Failed to cancel subscription",
    );
  }
};

/**
 * Resume a canceled subscription
 * @param {string} subscriptionId - Subscription ID
 * @returns {Promise<Object>} Resume result
 */
export const resumeSubscription = async (subscriptionId) => {
  try {
    const response = await api.post(`/subscriptions/${subscriptionId}/resume`);
    return response.data;
  } catch (error) {
    console.error("Failed to resume subscription:", error);
    throw new Error(
      error.response?.data?.message || "Failed to resume subscription",
    );
  }
};

/**
 * Get billing history
 * @param {Object} params - Query parameters (limit, starting_after, etc.)
 * @returns {Promise<Object>} Billing history
 */
export const getBillingHistory = async (params = {}) => {
  try {
    const response = await api.get("/billing/history", { params });
    return response.data;
  } catch (error) {
    console.error("Failed to get billing history:", error);
    throw new Error(
      error.response?.data?.message || "Failed to retrieve billing history",
    );
  }
};

/**
 * Download invoice
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Blob>} Invoice PDF blob
 */
export const downloadInvoice = async (invoiceId) => {
  try {
    const response = await api.get(`/billing/invoices/${invoiceId}/download`, {
      responseType: "blob",
    });

    return response.data;
  } catch (error) {
    console.error("Failed to download invoice:", error);
    throw new Error(
      error.response?.data?.message || "Failed to download invoice",
    );
  }
};

/**
 * Validate card information without creating a payment method
 * @param {Object} stripe - Stripe instance
 * @param {Object} cardElement - Stripe card element
 * @returns {Promise<Object>} Validation result
 */
export const validateCard = async (stripe, cardElement) => {
  try {
    // Create a temporary token to validate the card
    const result = await stripe.createToken(cardElement);

    if (result.error) {
      throw result.error;
    }

    return { valid: true, token: result.token };
  } catch (error) {
    return { valid: false, error: handleStripeError(error) };
  }
};

/**
 * Format amount for display
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code
 * @returns {string} Formatted amount
 */
export const formatAmount = (amount, currency = "usd") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
};

/**
 * Check if browser supports Payment Request API
 * @returns {boolean} Support status
 */
export const supportsPaymentRequest = () => {
  return window.PaymentRequest !== undefined;
};

export default {
  getStripe,
  handleStripeError,
  createPaymentIntent,
  createSubscription,
  confirmPayment,
  createPaymentMethod,
  updatePaymentMethod,
  getSubscription,
  cancelSubscription,
  resumeSubscription,
  getBillingHistory,
  downloadInvoice,
  validateCard,
  formatAmount,
  supportsPaymentRequest,
  STRIPE_ERROR_TYPES,
};
